import { HelpdeskAttachmentInput } from './HelpdeskAttachmentInput';
import { validateHelpdeskAttachments } from '../api/attachments';
import { useState, type FormEvent } from 'react';

import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Checkbox,
  Heading,
  Input,
  Label,
  NativeSelect,
  RequiredMark,
  Text,
  Textarea,
} from '@shared/ui';
import { z } from 'zod';
import { useRuntimeCreate } from '../model/runtime-mutations';
import type { HelpdeskOption, RequesterBootstrapResponse } from '../model/runtime';
const createHelpdeskTaskSchema = z.object({
  title: z.string().trim().min(1).max(300),
  message: z.string().trim().min(1).max(20000),
  requester: z
    .union([z.object({ customerId: z.uuid() }), z.object({ contactEmail: z.email().max(320) })])
    .optional(),
  originId: z.uuid(),
  feedbackCategoryId: z.uuid().optional(),
  privacyAcknowledged: z.literal(true),
});
export interface HelpdeskRequestFormProps {
  options:
    | RequesterBootstrapResponse['data']
    | { origins: readonly HelpdeskOption[]; feedbackCategories: readonly HelpdeskOption[] };
  customerId?: string;
  customerEmail?: string;
  privacyPolicyUrl: string;
  presentation?: 'card' | 'widget';
  onCreated: (taskUuid: string) => void;
  onCancel: () => void;
}
export function HelpdeskRequestForm({
  options: formOptions,
  customerId,
  customerEmail,
  privacyPolicyUrl,
  presentation = 'card',
  onCreated,
  onCancel,
}: HelpdeskRequestFormProps) {
  const { t } = useTranslation();
  const create = useRuntimeCreate();
  const requesterOptions = 'requester' in formOptions ? formOptions : null;
  const origins = 'origins' in formOptions ? formOptions.origins : [formOptions.origin];
  const fixedEmail =
    requesterOptions?.requester.mode === 'account'
      ? (requesterOptions.requester.email ?? '')
      : customerId
        ? (customerEmail ?? '')
        : null;
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [contactEmail, setContactEmail] = useState('');
  const [originId, setOriginId] = useState('');
  const [feedbackCategoryId, setFeedbackCategoryId] = useState('');
  const [invalid, setInvalid] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = createHelpdeskTaskSchema.safeParse({
      title,
      message,
      requester:
        requesterOptions?.requester.mode === 'account'
          ? undefined
          : customerId
            ? { customerId }
            : { contactEmail },
      originId: requesterOptions?.origin.id ?? originId,
      privacyAcknowledged,
      ...(feedbackCategoryId ? { feedbackCategoryId } : {}),
    });
    setInvalid(!result.success);
    if (!result.success || !validateHelpdeskAttachments(files)) return;
    create.mutate(
      { ...result.data, files },
      {
        onSuccess: (response) => {
          setTitle('');
          setMessage('');
          setFiles([]);
          setContactEmail('');
          setPrivacyAcknowledged(false);
          onCreated(response.data.id);
        },
      }
    );
  };
  const subjectField = (
    <div className="space-y-2">
      <Label htmlFor="helpdesk-create-title">
        {t('helpdesk.subject', 'Subject')}
        <RequiredMark />
      </Label>
      <Input
        id="helpdesk-create-title"
        value={title}
        maxLength={300}
        required
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
    </div>
  );
  return (
    <Card className={presentation === 'widget' ? 'border-0 shadow-none' : undefined}>
      <CardContent className={presentation === 'widget' ? 'space-y-4 p-0' : 'space-y-4 pt-6'}>
        {presentation === 'card' ? (
          <Heading as="h2" size="lg">
            {t('helpdesk.commands.create', 'Create request')}
          </Heading>
        ) : null}
        {origins.length === 0 ? (
          <Alert>
            <AlertTitle>
              {t('helpdesk.commands.noSources', 'No permitted request sources are available')}
            </AlertTitle>
          </Alert>
        ) : null}
        <form onSubmit={submit} className="space-y-4">
          <fieldset disabled={create.isPending} className="space-y-4">
            {presentation === 'card' ? subjectField : null}
            {fixedEmail !== null ? (
              <div className="space-y-2">
                <Label htmlFor="helpdesk-create-email">
                  {t('helpdesk.commands.contactEmail', 'Contact email')}
                </Label>
                <Input
                  id="helpdesk-create-email"
                  type="email"
                  autoComplete="email"
                  aria-describedby="helpdesk-create-email-hint"
                  value={fixedEmail}
                  readOnly
                />
                <Text id="helpdesk-create-email-hint" size="sm" color="fieldLabel">
                  {t(
                    'helpdesk.commands.profileRequester',
                    'Email comes from this customer profile; the request stays linked to it.'
                  )}
                </Text>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="helpdesk-create-email">
                  {t('helpdesk.commands.contactEmail', 'Contact email')}
                  <RequiredMark />
                </Label>
                <Input
                  id="helpdesk-create-email"
                  type="email"
                  autoComplete="email"
                  aria-describedby="helpdesk-create-email-hint"
                  value={contactEmail}
                  maxLength={320}
                  required
                  onChange={(event) => {
                    setContactEmail(event.target.value);
                  }}
                />
                <Text id="helpdesk-create-email-hint" size="sm" color="fieldLabel">
                  {t('helpdesk.widget.contactEmailHint', 'We will use this email to contact you.')}
                </Text>
              </div>
            )}
            <div className={presentation === 'widget' ? 'space-y-4' : 'grid gap-4 sm:grid-cols-2'}>
              {!requesterOptions ? (
                <div className="space-y-2">
                  <Label htmlFor="helpdesk-create-origin">
                    {t('helpdesk.origin', 'Source')}
                    <RequiredMark />
                  </Label>
                  <NativeSelect
                    id="helpdesk-create-origin"
                    value={originId}
                    required
                    onChange={(event) => {
                      setOriginId(event.target.value);
                    }}
                  >
                    <option value="">
                      {t('helpdesk.commands.chooseSource', 'Choose a source')}
                    </option>
                    {origins.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.display}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="helpdesk-create-category">
                  {t('helpdesk.commands.category', 'Feedback category')}
                </Label>
                <NativeSelect
                  id="helpdesk-create-category"
                  value={feedbackCategoryId}
                  onChange={(event) => {
                    setFeedbackCategoryId(event.target.value);
                  }}
                >
                  <option value="">{t('helpdesk.widget.notProvided', 'Not provided')}</option>
                  {formOptions.feedbackCategories.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.display}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
            {presentation === 'widget' ? subjectField : null}
            <div className="space-y-2">
              <Label htmlFor="helpdesk-create-message">
                {t('helpdesk.commands.message', 'Message')}
                <RequiredMark />
              </Label>
              <Textarea
                id="helpdesk-create-message"
                value={message}
                maxLength={20000}
                required
                rows={presentation === 'widget' ? 2 : 6}
                aria-describedby={invalid ? 'helpdesk-create-invalid' : undefined}
                aria-invalid={invalid}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setInvalid(false);
                }}
              />
            </div>
            <HelpdeskAttachmentInput
              id="helpdesk-create-files"
              compact={presentation === 'widget'}
              files={files}
              disabled={create.isPending}
              uploading={create.isPending && files.length > 0}
              onChange={setFiles}
            />
            <div className="flex items-center gap-3">
              <Checkbox
                id="helpdesk-privacy"
                className={presentation === 'widget' ? 'h-6 w-6 shrink-0' : undefined}
                required
                checked={privacyAcknowledged}
                onCheckedChange={(checked) => {
                  setPrivacyAcknowledged(checked === true);
                }}
              />
              <Label htmlFor="helpdesk-privacy" className="leading-normal">
                {t(
                  'helpdesk.widget.privacyAcknowledgement',
                  'I have read and accept the privacy policy.'
                )}{' '}
                <a
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {t('helpdesk.widget.privacyPolicy', 'Privacy policy')}
                </a>
              </Label>
            </div>
          </fieldset>
          {invalid ? (
            <Text id="helpdesk-create-invalid" role="alert" size="sm" color="destructive">
              {t(
                'helpdesk.commands.invalid',
                'Check the required fields and enter a non-empty message.'
              )}
            </Text>
          ) : null}
          {create.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                {t(
                  'helpdesk.commands.saveFailed',
                  'Could not save. Your draft is still here; you can try again.'
                )}
              </AlertTitle>
            </Alert>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <Button
              type="submit"
              className={presentation === 'widget' ? 'w-full' : undefined}
              disabled={
                create.isPending ||
                !privacyAcknowledged ||
                !origins.length ||
                !validateHelpdeskAttachments(files)
              }
            >
              {create.isPending
                ? t('helpdesk.commands.saving', 'Saving...')
                : presentation === 'widget'
                  ? t('helpdesk.widget.sendMessage', 'Send my message')
                  : requesterOptions
                    ? t('helpdesk.commands.create', 'Create request')
                    : t('helpdesk.commands.createTask', 'Create task')}
            </Button>
            {presentation === 'card' ? (
              <Button
                type="button"
                variant="outline"
                disabled={create.isPending}
                onClick={onCancel}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
